

import { Share2, Link2, QrCode } from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Technique } from '@/types/technique';
import { useTechniqueSharing } from './useTechniqueSharing';
import { ShareLinkTab } from '@/components/game-planner/share-dialog/ShareLinkTab';
import { QRCodeTab } from '@/components/game-planner/share-dialog/QRCodeTab';
import { ShareError } from '@/components/game-planner/share-dialog/ShareError';
import { ShareLoadingState } from '@/components/game-planner/share-dialog/LoadingState';

interface ShareTechniqueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  technique: Technique | null;
}

export function ShareTechniqueDialog({ open, onOpenChange, technique }: ShareTechniqueDialogProps) {
  const {
    shareUrl,
    copied,
    isLoading,
    error,
    errorDetails,
    generateShareLink,
    copyToClipboard
  } = useTechniqueSharing(technique, open);

  const errorDetailsMessage =
    errorDetails instanceof Error
      ? errorDetails.message
      : typeof errorDetails === 'string'
      ? errorDetails
      : null;
  
  const handleRetry = () => {
    generateShareLink();
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share Technique
          </DialogTitle>
          <DialogDescription>
            Share this technique with other BJJ practitioners
          </DialogDescription>
        </DialogHeader>
        
        {isLoading ? (
          <ShareLoadingState />
        ) : error ? (
          <ShareError
            error={error}
            errorDetails={errorDetailsMessage}
            onRetry={handleRetry}
          />
        ) : (
          <Tabs defaultValue="link" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="link" className="flex items-center">
                <Link2 className="w-4 h-4 mr-2" />
                Link
              </TabsTrigger>
              <TabsTrigger value="qrcode" className="flex items-center">
                <QrCode className="w-4 h-4 mr-2" />
                QR Code
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="link" className="pt-4">
              <ShareLinkTab 
                shareUrl={shareUrl || ''}
                copied={copied} 
                onCopy={copyToClipboard} 
              />
            </TabsContent>
            
            <TabsContent value="qrcode" className="pt-4">
              <QRCodeTab shareUrl={shareUrl || ''} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
